<template>
  <!-- Visible output -->
  <template v-if="mode === 'full'">
    <ion-button
      v-for="action in actions"
      :key="action.label"
      :color="action.color"
      :disabled="action.disabled"
      @click="action.handler()"
    >
      <ion-icon v-if="action.icon" slot="start" :icon="action.icon" />
      {{ action.label }}
    </ion-button>
  </template>
  <template v-else-if="mode === 'labels'">
    <ion-button
      v-for="action in actions"
      :key="action.label"
      :color="action.color"
      :disabled="action.disabled"
      @click="action.handler()"
    >
      {{ action.label }}
    </ion-button>
  </template>
  <template v-else-if="mode === 'icons'">
    <ion-button
      v-for="action in actions"
      :key="action.label"
      :color="action.color"
      :disabled="action.disabled"
      @click="action.handler()"
    >
      <ion-icon v-if="action.icon" slot="icon-only" :icon="action.icon" />
      <template v-else>{{ action.label }}</template>
    </ion-button>
  </template>
  <template v-else>
    <ion-button :title="title" @click="openSheet">
      <ion-icon
        slot="icon-only"
        :ios="ellipsisHorizontalCircleOutline"
        :md="ellipsisVertical"
      />
    </ion-button>
  </template>

  <!-- Measurement shelf (auto mode only) — always off-screen, never visible -->
  <div v-if="auto" ref="measureShelfRef" class="overflow-measure-shelf" aria-hidden="true">
    <div ref="fullMeasureRef" style="display:flex">
      <ion-button v-for="action in actions" :key="action.label">
        <ion-icon v-if="action.icon" slot="start" :icon="action.icon" />
        {{ action.label }}
      </ion-button>
    </div>
    <div ref="labelsMeasureRef" style="display:flex">
      <ion-button v-for="action in actions" :key="action.label">
        {{ action.label }}
      </ion-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watchEffect, useSlots, onBeforeUnmount, Fragment } from 'vue';
import type { VNode } from 'vue';
import { IonButton, IonIcon, actionSheetController } from '@ionic/vue';
import { ellipsisHorizontalCircleOutline, ellipsisVertical } from 'ionicons/icons';
import { useMediaQuery, useResizeObserver } from '@vueuse/core';
import OverflowActionItem from './OverflowActionItem.vue';

export interface OverflowAction {
  label: string;
  icon?: string;
  color?: string;
  handler: () => void;
  disabled?: boolean;
}

const ICON_BTN_PX = 44;

const props = withDefaults(defineProps<{
  title?: string;
  auto?: boolean;
  /** Static mode breakpoints (viewport px). Ignored when auto=true. */
  fullBreakpoint?: number;
  labelsBreakpoint?: number;
  iconsBreakpoint?: number;
}>(), {
  title: 'More actions',
  auto: true,
  fullBreakpoint: 768,
  labelsBreakpoint: 600,
  iconsBreakpoint: 430,
});

// ── Slot → actions extraction ─────────────────────────────────────────────────

const slots = useSlots();
const actions = ref<OverflowAction[]>([]);

function extractActions(vnodes: VNode[]): OverflowAction[] {
  return vnodes.flatMap((vn) => {
    if (vn.type === Fragment)
      return extractActions(Array.isArray(vn.children) ? (vn.children as VNode[]) : []);
    if (vn.type !== OverflowActionItem) return [];
    return [{
      label:    vn.props?.label    ?? '',
      icon:     vn.props?.icon,
      color:    vn.props?.color,
      disabled: vn.props?.disabled ?? false,
      handler:  () => vn.props?.onClick?.(),
    }];
  });
}

watchEffect(() => {
  actions.value = extractActions(slots.default?.() ?? []);
  updateMeasurements();
});

// ── Static mode ──────────────────────────────────────────────────────────────

const isOverflowStatic = useMediaQuery(computed(() => `(max-width: ${props.iconsBreakpoint}px)`));
const isIconsStatic    = useMediaQuery(computed(() => `(max-width: ${props.labelsBreakpoint}px)`));
const isLabelsStatic   = useMediaQuery(computed(() => `(max-width: ${props.fullBreakpoint}px)`));

const staticMode = computed(() =>
  isOverflowStatic.value ? 'overflow'
  : isIconsStatic.value ? 'icons'
  : isLabelsStatic.value ? 'labels'
  : 'full'
);

// ── Auto mode ─────────────────────────────────────────────────────────────────

const measureShelfRef  = ref<HTMLElement | null>(null);
const fullMeasureRef   = ref<HTMLElement | null>(null);
const labelsMeasureRef = ref<HTMLElement | null>(null);

const containerWidth     = ref(0);
const fullMeasureWidth   = ref(0);
const labelsMeasureWidth = ref(0);

function updateMeasurements() {
  if (!props.auto) return;
  nextTick(() => {
    fullMeasureWidth.value   = fullMeasureRef.value?.offsetWidth  ?? 0;
    labelsMeasureWidth.value = labelsMeasureRef.value?.offsetWidth ?? 0;
  });
}

useResizeObserver(measureShelfRef, (entries) => {
  const parent = measureShelfRef.value?.parentElement;
  containerWidth.value = parent?.offsetWidth ?? entries[0]?.contentRect.width ?? 0;
  updateMeasurements();
});

const autoMode = computed(() => {
  const w = containerWidth.value;
  const iconsWidth = actions.value.length * ICON_BTN_PX;
  if (w >= fullMeasureWidth.value   && fullMeasureWidth.value > 0)   return 'full';
  if (w >= labelsMeasureWidth.value && labelsMeasureWidth.value > 0) return 'labels';
  if (w >= iconsWidth && iconsWidth > 0) return 'icons';
  return 'overflow';
});

// ── Combined ──────────────────────────────────────────────────────────────────

const mode = computed(() => props.auto ? autoMode.value : staticMode.value);

// ── Action sheet ──────────────────────────────────────────────────────────────

// Created through the controller, not as an inline action sheet element: an inline overlay is teleported to `ion-app` and
// leaves a placeholder comment behind, and its dismiss throws in `removeViewFromDom` if Vue removed that placeholder's
// parent in the meantime (the handler of a tapped action changes `actions` and so possibly `mode`; AMS-VUE-4).
let openSheetElement: HTMLIonActionSheetElement | null = null;
let sheetCreating = false;

const sheetButtons = computed(() => [
  ...actions.value.map((action) => ({
    text: action.label,
    icon: action.icon,
    role: action.color === 'danger' ? 'destructive' : undefined,
    handler: () => {
      if (!action.disabled) action.handler();
    },
  })),
  { text: 'Cancel', role: 'cancel' },
]);

async function openSheet(): Promise<void> {
  if (openSheetElement || sheetCreating) return;
  sheetCreating = true;
  let sheet: HTMLIonActionSheetElement;
  try {
    sheet = await actionSheetController.create({ buttons: sheetButtons.value });
  } finally {
    sheetCreating = false;
  }
  openSheetElement = sheet;
  sheet.onDidDismiss().then(() => {
    if (openSheetElement === sheet) openSheetElement = null;
  });
  await sheet.present();
}

onBeforeUnmount(() => {
  openSheetElement?.dismiss();
  openSheetElement = null;
});
</script>

<style scoped>
.overflow-measure-shelf {
  position: absolute;
  top: -9999px;
  left: 0;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
}
</style>
